import React, { useEffect } from 'react';
import fr from 'facet-rest';
import {
  FDIcon,
  FDPaper,
  FDPrimaryActionButton,
  FDSecondaryActionButton,
  FDStack,
  FDTitleText,
  FDConfirmationDialog,
  FDHeadlineText, FDSelectInput,
} from '@facet-wealth/design';
import MetaControlField from 'Components/UI/Meta/MetaForm/Components/MetaControlField';
import useMetaLayoutContext from 'Components/UI/Meta/MetaForm/Components/MetaLayoutContext';
import { useSnackbarContext } from 'Context/SnackbarContext';
import { usePageContentContext } from 'Views/Page/Contexts/PageContentContext';
import { ErrorList2 } from 'Components/ComponentLibrary/Molecules/ErrorList2';
import { dataAttrs } from 'Helpers/Testing';
import {
  TaxDiagnosticNotes,
} from 'Views/Page/Content/Templates/TaxDiagnosticPlannerPage/TaxDiagnosticTemplate/TaxDiagnosticNotes';


const CARD_WIDTH = '720px';

export const Layout = () => {
  const { loadPageContentLink, refreshPageContentCollection } = usePageContentContext();
  const { resource } = useMetaLayoutContext();
  const { displaySuccess } = useSnackbarContext();
  const [open, setOpen] = React.useState(false);
  const [loading, setLoading] = React.useState(false);
  const [errors, setErrors] = React.useState([]);

  const handleOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleSaveAsDraft = async (onSubmitSuccess) => {
    setErrors([]);
    try {
      setLoading(true);
      const [submittedResource] = await resource.link('self').put();
      onSubmitSuccess(submittedResource);
    } catch (err) {
      setErrors(err.errors);
    } finally {
      setLoading(false);
    }
  };

  const handlePreviewCurrent = async (relationship) => {
    await handleSaveAsDraft((_) => loadPageContentLink(relationship.link('related')));
  };

  const handlePreview = async () => {
    await handleSaveAsDraft((submittedResource) => loadPageContentLink(loadPageContentLink(submittedResource.relationship(fr.relationships.preview.name).link('related'))));
  };

  const handlePreviewSummary = async () => {
    await handleSaveAsDraft((submittedResource) => loadPageContentLink(loadPageContentLink(submittedResource.relationship(fr.relationships.previewSummaryPage.name).link('related'))));
  };

  const handleSend = async () => {
    setErrors([]);
    try {
      setLoading(true);
      await resource.link('self').post(); // Post is publish
      displaySuccess('Tax diagnostic sent to member.');
      await refreshPageContentCollection();
    } catch (err) {
      setErrors(err.errors);
    } finally {
      setLoading(false);
      handleClose();
    }
  };

  // We need to be able to use the selected taxYear to get the contribution limits for many of the values in this
  // form such as IRA, HSA, etc because the limits vary by year. This means our maxFns need to pull the tax year
  // from the most recently saved tax diagnostic (or default to this year) so whenever this value changes we save
  // the tax diagnostic and refresh the page so that the max values are correct.
  const handleOnChangeTaxYear = async (val) => {
    resource.attr('taxYear', val);
    await handleSaveAsDraft((_) => refreshPageContentCollection());
  };

  useEffect(() => {
    // If we landed on this page without an ID we should save it as draft because it makes literally everything easier downstream
    // if we have a valid taxDiagnosticPlannerPage resource id
    if (resource.id() === '0') {
      handleSaveAsDraft((_) => refreshPageContentCollection());
    }
  }, [resource]);

  return (
    <FDStack paddingY={3} spacing={2} direction="column" alignItems="center">
      <FDHeadlineText
        color={'textSecondary'}
        value={(
          <FDStack direction="column" alignItems="center" spacing={1}>
            <MetaControlField attr="title" {...dataAttrs('tax-diagnostic-title')} />
          </FDStack>
        )}
      />
      <MetaControlField attr={'description'} />

      <FDStack direction="row" spacing={2} justifyContent={'center'} alignItems={'flex-start'}>
        <FDSelectInput
          onChange={(val) => handleOnChangeTaxYear(val)}
          value={resource.attr('taxYear')}
          options={resource.meta().field('taxYear').options().list()
            .map(option => ({
              id: option.attr('id'),
              prompt: option.attr('prompt'),
            }))}
          label={resource.meta().field('taxYear').attr('prompt')}
        />
        <MetaControlField attr="totalValue" />
        <MetaControlField attr="hideTotalValue" />
      </FDStack>

      <FDStack direction={'row'} spacing={4}>
        <FDStack direction={'column'} spacing={2}>
          <FDPaper padding={2} appearance="elevated" width={CARD_WIDTH}>
            <FDStack direction="column" spacing={2}>
              <FDIcon icon="dollarSign" />
              <FDTitleText value="Saving strategies" />
              <MetaControlField attr="megaBackdoorRoth401KSavingAmount" />
              <MetaControlField attr="spouseMegaBackdoorRoth401KSavingAmount" />
              <FDStack direction="row" spacing={2}>
                <FDStack width="275px">
                  <MetaControlField attr="savingStrategyIRAType" flex={1} />
                </FDStack>
                <MetaControlField attr="savingStrategyAmount" flex={1} />
                <MetaControlField attr="savingStrategySplitAmount" flex={1} />
              </FDStack>
              {resource.isTruthy('hasSpouseMember') ? (
                <FDStack direction="row" spacing={2}>
                  <FDStack width="275px">
                    <MetaControlField attr="spouseSavingStrategyIRAType" flex={1} />
                  </FDStack>
                  <MetaControlField attr="spouseSavingStrategyAmount" flex={1} />
                  <MetaControlField attr="spouseSavingStrategySplitAmount" flex={1} />
                </FDStack>
              ) : null}
              <FDStack direction="row" spacing={2}>
                <FDStack width="275px">
                  <MetaControlField attr="employerSponsoredPlanType" flex={1} />
                </FDStack>
                <MetaControlField attr="employerSponsoredPlanAmount" flex={1} />
                <MetaControlField attr="employerSponsoredPlanSplitAmount" flex={1} />
              </FDStack>
              {resource.isTruthy('hasSpouseMember') ? (
                <FDStack direction="row" spacing={2}>
                  <FDStack width="275px">
                    <MetaControlField attr="spouseEmployerSponsoredPlanType" flex={1} />
                  </FDStack>
                  <MetaControlField attr="spouseEmployerSponsoredPlanAmount" flex={1} />
                  <MetaControlField attr="spouseEmployerSponsoredPlanSplitAmount" flex={1} />
                </FDStack>
              ) : null}
              <MetaControlField attr="hsaSavingAmount" />
              <MetaControlField attr="spouseHSASavingAmount" />
              <MetaControlField attr="fsaSavingAmount" />
              <MetaControlField attr="spouseFSASavingAmount" />
              <MetaControlField attr={'sepIRAAmount'} />
              <MetaControlField attr={'spouseSepIRAAmount'} />
              <FDStack direction="row" spacing={2}>
                <FDStack width="275px">
                  <MetaControlField attr={'simpleIRAType'} flex={1} />
                </FDStack>
                <MetaControlField attr={'simpleIRAAmount'} flex={1} />
                <MetaControlField attr={'simpleIRASplitAmount'} flex={1} />
              </FDStack>
              {resource.isTruthy('hasSpouseMember') ? (
                <FDStack direction="row" spacing={2}>
                  <FDStack width="275px">
                    <MetaControlField attr={'spouseSimpleIRAType'} flex={1} />
                  </FDStack>
                  <MetaControlField attr={'spouseSimpleIRAAmount'} flex={1} />
                  <MetaControlField attr={'spouseSimpleIRASplitAmount'} flex={1} />
                </FDStack>) : null }
              <FDStack direction="row" spacing={2}>
                <FDStack width="275px">
                  <MetaControlField attr={'inheritedIRAType'} flex={1} />
                </FDStack>
                <MetaControlField attr={'inheritedIRAAmount'} flex={1} />
                <MetaControlField attr={'inheritedIRASplitAmount'} flex={1} />
              </FDStack>
              {resource.isTruthy('hasSpouseMember') ? (
                <FDStack direction="row" spacing={2}>
                  <FDStack width="275px">
                    <MetaControlField attr={'spouseInheritedIRAType'} flex={1} />
                  </FDStack>
                  <MetaControlField attr={'spouseInheritedIRAAmount'} flex={1} />
                  <MetaControlField attr={'spouseInheritedIRASplitAmount'} flex={1} />
                </FDStack>) : null }
              <MetaControlField attr={'deferredCompAmount'} />
              <MetaControlField attr={'spouseDeferredCompAmount'} />
            </FDStack>
          </FDPaper>

          <FDPaper padding={2} appearance="elevated" width={CARD_WIDTH}>
            <FDStack direction="column" spacing={2}>
              <FDIcon icon="starChart" />
              <FDTitleText value="Credits and deductions" />
              <FDStack direction="row" spacing={2}>
                <FDStack width="275px">
                  <MetaControlField attr="hasLifetimeLearningCredit" flex={1} />
                </FDStack>
                <MetaControlField attr="lifetimeLearningCreditAmount" flex={1} />
              </FDStack>
              <FDStack direction="row" spacing={2}>
                <FDStack width="275px">
                  <MetaControlField attr="hasStudentLoanInterestDeduction" flex={1} />
                </FDStack>
                <MetaControlField attr="studentLoanInterestDeductionAmount" flex={1} />
              </FDStack>
              <FDStack direction="row" spacing={2}>
                <FDStack width="275px">
                  <MetaControlField attr="hasChildTaxCredit" flex={1} />
                </FDStack>
                <MetaControlField attr="childTaxCreditAmount" flex={1} />
              </FDStack>
              <FDStack direction="row" spacing={2}>
                <FDStack width="275px">
                  <MetaControlField attr="hasCleanVehicleCredit" flex={1} />
                </FDStack>
                <MetaControlField attr="cleanVehicleCreditAmount" flex={1} />
              </FDStack>
              <FDStack direction="row" spacing={2}>
                <FDStack width="275px">
                  <MetaControlField attr="hasQualifiedAdoptionExpensesCredit" flex={1} />
                </FDStack>
                <MetaControlField attr="qualifiedAdoptionExpensesCreditAmount" flex={1} />
              </FDStack>
              <FDStack direction="row" spacing={2}>
                <FDStack width="275px">
                  <MetaControlField attr="hasAmericanOpportunityCredit" flex={1} />
                </FDStack>
                <MetaControlField attr="americanOpportunityCreditAmount" flex={1} />
              </FDStack>
            </FDStack>
          </FDPaper>

          <FDPaper padding={2} appearance="elevated" width={CARD_WIDTH}>
            <FDStack direction="column" spacing={2}>
              <FDIcon icon="starChart" />
              <FDTitleText value="Taxable account strategies" />
              <FDStack direction="column" spacing={2}>
                <MetaControlField attr="carryForwardLossesAmount" flex={1} />
                <MetaControlField attr="diversifyDueToCarryForwardLossesAmount" flex={1} />
                <MetaControlField attr="realizingGainsThisYear" flex={1} />
                <MetaControlField attr="diversifyFromGainsBudgetAmount" flex={1} />
                <MetaControlField attr="realizedGainsTaxGoals" flex={1} />
              </FDStack>
            </FDStack>
          </FDPaper>

          <FDPaper padding={2} appearance="elevated" width={CARD_WIDTH}>
            <FDStack direction="column" spacing={2}>
              <FDIcon icon="starChart" />
              <FDTitleText value="Strategies for tax free growth" />
              <FDStack direction="column" spacing={2}>
                <MetaControlField attr="convertToRothAmount" flex={1} />
                <MetaControlField attr="convertNextYear" flex={1} />
                <MetaControlField attr="rothConversionTaxGoals" flex={1} />
              </FDStack>
            </FDStack>
          </FDPaper>

          <FDPaper padding={2} appearance="elevated" width={CARD_WIDTH}>
            <FDStack direction="column" spacing={2}>
              <FDIcon icon="umbrella" />
              <FDTitleText value="Education savings" />
              <FDStack direction="column" spacing={2}>
                <MetaControlField attr="educationDeductAmount" />
                <MetaControlField attr="state529Plan" />
                <MetaControlField attr="state" />
              </FDStack>
            </FDStack>
          </FDPaper>
          <FDPaper padding={2} appearance="elevated" width={CARD_WIDTH}>
            <FDStack direction="column" spacing={2}>
              <FDIcon icon="dollarSign" />
              <FDTitleText value="Analysis" />
              <FDStack direction="column" spacing={2}>
                <MetaControlField attr="hasFilingStatusMistake" />
                <MetaControlField attr="hasTakenRMDNotRequiredLastYear" />
                <MetaControlField attr="hasMissedHSAOr529Deductions" />
                <MetaControlField attr="hasMissed8606ForRothConversions" />
                <FDStack spacing={2}>
                  <MetaControlField attr="hasMissed1099OnTaxableAccounts" flex={1} />
                  <MetaControlField attr="auditMissing1099Detail" flex={1} />
                </FDStack>
              </FDStack>
            </FDStack>
          </FDPaper>
          {errors.length > 0 ? <FDStack flex={1} justifyContent="center"><FDPaper appearance={'elevated'} padding={2} width={CARD_WIDTH}><ErrorList2 errors={errors} /></FDPaper></FDStack> : null}
        </FDStack>

        <FDStack flex={1} justifyContent="center">
          {resource.withRelationship(fr.relationships.notes.name,
            (rel) => (<TaxDiagnosticNotes relationship={rel} />),
            () => null)}
        </FDStack>

      </FDStack>

      <FDStack direction="row" spacing={2} justifyContent="center">
        {resource.withRelationship(fr.relationships.previewCurrent.name,
          (rel) => (<FDSecondaryActionButton
            label={rel.meta('prompt')}
            loading={loading}
            onClick={() => handlePreviewCurrent(rel)}
            {...dataAttrs('preview-current')}
          />),
          () => null)}
        {/* The following two buttons need to exist even if the relationship does not exist because we must first put
        the resource and get the correct related links for navigation from the submitted resource */}
        <FDSecondaryActionButton
          label={resource.attr('saveAndPreviewSummaryPrompt')}
          loading={loading}
          onClick={handlePreviewSummary}
          {...dataAttrs('save-and-preview-summary')}
        />
        <FDSecondaryActionButton
          label={resource.attr('saveAndPreviewPrompt')}
          loading={loading}
          onClick={handlePreview}
          {...dataAttrs('save-and-preview')}
        />
        <FDPrimaryActionButton
          label="Send to member"
          disabled={!resource.meta().action('POST')}
          onClick={handleOpen}
          loading={loading}
          {...dataAttrs('send-to-member')}
        />
      </FDStack>
      <FDConfirmationDialog
        title="Confirm Tax Diagnostic"
        message="You are about to send this tax diagnostic to the member. Are you sure you want to proceed?"
        confirmLabel="Confirm & Send"
        open={open}
        onConfirm={handleSend}
        onClose={handleClose}
        loading={loading}
      />
    </FDStack>
  );
};

Layout.defaultProps = {};

Layout.propTypes = {};
